import React, {useState} from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  ResponsiveValue,
  Text,
  VStack,
} from '@chakra-ui/react';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {TPath, useTypedQuery} from '@synako/typed-path';
import {
  FiltresItemCatalogueGrandCompte,
  routes,
  useDebouncedCallback,
} from 'ff-common';
import {useMe, useNomenclature} from '@/haligator-factory';
import {HaliRenderOne} from '@synako/haligator';
import {imageOptimisee} from '../../mediatheque/optimisation';
import {LOGO_FF_COULEUR_URL} from '../../mediatheque/logoFFCouleurUrl';
import {ContactCommercialIcon2} from '@/components/svg/ContactCommercialIcon2';
// import {CartIcon} from '@/components/svg/CartIcon';
import {SwitchIcon} from '@/components/svg/SwitchIcon';
import {UserIcon} from '@/components/svg/UserIcon';
import {SearchIcon} from '@/components/svg/SearchIcon';
import {ChevronDownIcon, CloseIcon} from '@chakra-ui/icons';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {useHalState} from '@synako/haligator';
import {showContactsModal} from './ContactsModal';

interface HeaderProps {
  backoffice?: boolean;
  filtersHook: ReturnType<
    typeof useTypedQuery<TPath<{Query: FiltresItemCatalogueGrandCompte}>>
  >;
}

// Common style objects to reduce duplication
const styles = {
  searchContainer: {
    spacing: '0',
    borderRadius: 8,
    border: '2px solid',
    borderColor: 'gray.200',
    bg: 'white',
  },
  userMenuTrigger: {
    _hover: {bg: 'gray.200'},
    bg: 'gray.100',
    p: '2',
    cursor: 'pointer',
    borderRadius: 'full',
  },
};

/**
 * Logo avec lien de navigation
 */
const LogoLink: React.FC<{
  backoffice: boolean;
  size: number;
  width: string | Record<string, string | number>;
  display?: ResponsiveValue<string>;
}> = ({backoffice, size, width, display}) => {
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();
  return (
    <Link
      as={RouterLink}
      to={
        backoffice
          ? routes.client.backOfficeCatalogueGrandCompte.path
          : buildClientRoute(routes.client.catalogueGrandCompte)
      }
    >
      <Image
        src={imageOptimisee(LOGO_FF_COULEUR_URL, size)}
        alt={t('global.alt_logo_france_frais')}
        w={width}
        h='auto'
        display={display}
      />
    </Link>
  );
};

/**
 * Menu utilisateur avec popover
 */
const UserMenuPopover: React.FC<{backoffice: boolean}> = ({backoffice}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();
  //Should throw si dans le backoffice
  const user = useHalState(useMe());
  const clientSwitchHook = useHalState(useMe().follow('switch'));

  return (
    <Popover placement='bottom-end'>
      <PopoverTrigger>
        <HStack {...styles.userMenuTrigger}>
          <Avatar
            name={user.data?.name}
            bg='green.500'
            color='white'
            size='sm'
          />
          <ChevronDownIcon />
        </HStack>
      </PopoverTrigger>
      <PopoverContent minW='200px'>
        <PopoverBody
          p={2}
          bg='white'
        >
          <VStack
            align='stretch'
            spacing={1}
          >
            {/* Bouton "Passer commande" masqué pour les clients grand compte (société 100)
               pour éviter le calcul du catalogue de 90000 articles */}
            {/* <Button variant="ghost"
              size="sm"
              justifyContent="flex-start"
              leftIcon={<CartIcon color={'gray.dark'} />}
              onClick={() => {
                if (backoffice) {
                  //Temporaire, à voir comment on gère les client BO ayant accès à la commande
                  navigate(buildClientRoute(routes.client.backOffice));
                } else {
                  navigate(buildClientRoute(routes.client.accueil));
                }
              }}>
              {t('menu.passer_commande')}
            </Button> */}
            {/* Only show "changer_de_client" button if user has multiple clients */}
            {(clientSwitchHook.data?.items.length ?? 0) > 1 && (
              <Button
                variant='ghost'
                size='sm'
                justifyContent='flex-start'
                leftIcon={<Icon as={SwitchIcon} />}
                onClick={() => {
                  navigate(buildClientRoute(routes.client.clientSelect));
                }}
              >
                {t('menu.changer_de_client')}
              </Button>
            )}
            {!backoffice && (
              <Button
                variant='ghost'
                size='sm'
                justifyContent='flex-start'
                display={{base: 'flex', lg: 'none'}}
                leftIcon={
                  <ContactCommercialIcon2
                    color={'gray.dark'}
                    width={'20px'}
                  />
                }
                onClick={() => {
                  showContactsModal();
                }}
              >
                {t('mes_informations.contacter_commercial')}
              </Button>
            )}
            <Button
              variant='ghost'
              size='sm'
              justifyContent='flex-start'
              leftIcon={<UserIcon color={'gray.dark'} />}
              as='a'
              href='/authn/logout'
            >
              {t('menu.deconnecter')}
            </Button>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Barre de recherche avec sélecteur de catégories
 */
export const SearchWithCategories: React.FC<{
  localSearchValue: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  filtersHook: HeaderProps['filtersHook'];
  buttonHeight: string | Record<string, string | number>;
  inputHeight: string | Record<string, string | number>;
  buttonFontSize?: string | Record<string, string | number>;
}> = ({
  localSearchValue,
  handleSearchChange,
  onClearSearch,
  filtersHook,
  buttonHeight,
  inputHeight,
  buttonFontSize,
}) => {
  const {t} = useTranslation();
  const nomenclatureNav = useNomenclature();

  // Détermine le texte à afficher pour la catégorie sélectionnée
  const getCategoryDisplay = (data: {
    web: {categories: {id: string; id_parent: string; libelle: string}[]};
  }) => {
    if (!filtersHook.params.cat_web) return t('grand_compte.categories');

    const selectedCategory = data.web.categories.find((cat) =>
      filtersHook.params.cat_web?.includes(cat.id),
    );
    if (!selectedCategory) return t('grand_compte.categories');

    // Traverse les catégories parentes pour trouver la plus haute
    let currentCategory = selectedCategory;
    while (currentCategory.id_parent) {
      const parentCategory = data.web.categories.find(
        (cat) => cat.id === currentCategory.id_parent,
      );
      if (!parentCategory) break;
      currentCategory = parentCategory;
    }
    return currentCategory.libelle || t('grand_compte.categories');
  };

  // Navigation vers une catégorie
  const navigateToCategory = (categoryId: string) => {
    filtersHook.change('cat_web', categoryId);
  };

  return (
    <HStack {...styles.searchContainer}>
      <Box position='relative'>
        <Popover placement='bottom-start'>
          <PopoverTrigger>
            <Button
              variant='outline'
              rightIcon={<ChevronDownIcon />}
              borderRight='none'
              borderTopRightRadius='0'
              borderBottomRightRadius='0'
              borderColor='gray.200'
              height={buttonHeight}
              fontSize={buttonFontSize}
              px={{base: 2, sm: 3, lg: 3}}
            >
              <HaliRenderOne
                nav={nomenclatureNav}
                render={({data}) => getCategoryDisplay(data)}
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent width='fit-content'>
            <PopoverBody
              p={2}
              bg='white'
            >
              <HaliRenderOne
                nav={nomenclatureNav}
                render={({data}) => (
                  <VStack
                    align='stretch'
                    spacing={1}
                  >
                    {/* Bouton toutes les catégories */}
                    <Button
                      variant='ghost'
                      size='sm'
                      justifyContent='flex-start'
                      onClick={() => {
                        filtersHook.change('cat_web', undefined);
                      }}
                    >
                      {t('grand_compte.categories')}
                    </Button>
                    {data.web.categories
                      .filter((categorie) => !categorie.id_parent)
                      .map((categorie) => (
                        <Button
                          key={categorie.id}
                          variant='ghost'
                          size='sm'
                          justifyContent='flex-start'
                          onClick={() => {
                            navigateToCategory(categorie.id);
                          }}
                        >
                          {categorie.libelle}
                        </Button>
                      ))}
                  </VStack>
                )}
              />
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>

      <InputGroup flex='1'>
        <InputLeftElement pointerEvents='auto'>
          <SearchIcon />
        </InputLeftElement>
        <Input
          variant='outline'
          placeholder={t('grand_compte.rechercher_placeholder')}
          value={localSearchValue}
          onChange={handleSearchChange}
          height={inputHeight}
          borderLeftRadius='0'
          borderTopLeftRadius='0'
          borderBottomLeftRadius='0'
          borderColor='gray.200'
          _focus={{borderColor: 'blue.main', boxShadow: 'outline'}}
        />
        {localSearchValue && (
          <InputRightElement>
            {/* Bouton pour effacer rapidement la recherche */}
            <IconButton
              aria-label='Effacer la recherche'
              size='sm'
              variant='ghost'
              _hover={{bg: 'none'}}
              icon={<CloseIcon boxSize={3} />}
              onClick={onClearSearch}
            />
          </InputRightElement>
        )}
      </InputGroup>
    </HStack>
  );
};

/**
 * Composant Header pour le catalogue grand compte
 */
export const CatalogueGrandCompteHeader: React.FC<HeaderProps> = ({
  backoffice = false,
  filtersHook,
}) => {
  // Local state for the search input value
  const [localSearchValue, setLocalSearchValue] = useState(
    filtersHook.params.q ?? '',
  );

  const {t} = useTranslation();

  // Debounce the function that updates the search query parameter
  const debouncedUpdateQueryParams = useDebouncedCallback(
    (query: string | undefined) => {
      // Update query and reset page to 1
      filtersHook.change('q', query);
      filtersHook.change('page', 1);
    },
    300,
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalSearchValue(newValue);
    debouncedUpdateQueryParams(newValue || undefined);
  };

  // Efface la recherche locale et réinitialise le filtre 'q'
  const handleClearSearch = () => {
    setLocalSearchValue('');
    debouncedUpdateQueryParams(undefined);
  };

  const handleContactClick = () => {
    showContactsModal();
  };

  return (
    <Box
      w='100%'
      bg='white'
      borderBottom='1px solid'
      borderColor='gray.light'
      px={{base: 3, md: 4, xl: 20}}
      py={{base: 2, md: 3}}
    >
      {/* Version mobile Top Row (Logo & User Menu) */}
      <Flex
        display={{base: 'flex', md: 'none'}}
        justify='space-between'
        align='center'
        mb={3}
        w='100%'
      >
        <LogoLink
          backoffice={backoffice}
          size={100}
          width='40px'
        />
        <UserMenuPopover backoffice={backoffice} />
      </Flex>

      {/* Main Layout */}
      <HStack
        flexDirection={{base: 'column', md: 'row'}}
        w='100%'
        alignItems='center'
        spacing={{md: 3, lg: 4}}
      >
        {/* Desktop Logo (Hidden on Mobile) */}
        <LogoLink
          backoffice={backoffice}
          display={{base: 'none', md: 'block'}}
          size={150}
          width={{md: '40px', lg: '50px'}}
        />

        {/* Search Bar Wrapper (Full width mobile, flex desktop) */}
        <Box
          flex={{md: '1'}}
          w={{base: '100%', md: 'auto'}}
          mb={{base: 3, md: 0}}
        >
          <SearchWithCategories
            localSearchValue={localSearchValue}
            handleSearchChange={handleSearchChange}
            onClearSearch={handleClearSearch}
            filtersHook={filtersHook}
            buttonHeight={{base: '40px', sm: '42px'}}
            inputHeight={{base: '40px', sm: '42px'}}
            buttonFontSize={{md: 'sm', lg: 'md'}}
          />
        </Box>

        {/* Section service client & Desktop User Menu */}
        <HStack spacing={{md: 2, lg: 3}}>
          {/* Contact Info (Hidden Mobile/Tablet) */}
          {!backoffice && (
            <HStack
              display={{base: 'none', lg: 'flex'}}
              spacing={2}
            >
              <Box
                padding='10px'
                paddingTop='12px'
                paddingBottom='12px'
                bg='blue.main_transparency_10'
                borderRadius='8px'
                position={'relative'}
              >
                <ContactCommercialIcon2 color={'blue.main'} />
              </Box>
              <VStack
                spacing='0'
                alignItems='flex-start'
              >
                <Text
                  color='blue.500'
                  fontWeight='bold'
                >
                  {t('grand_compte.sav_question')}
                </Text>
                <Link
                  color='blue.500'
                  textDecorationLine={'underline'}
                  onClick={handleContactClick}
                  fontSize='sm'
                >
                  {t('mes_informations.contacter_commercial')}
                </Link>
              </VStack>
            </HStack>
          )}

          {/* Desktop User Menu (Hidden Mobile) */}
          <Box
            position='relative'
            display={{base: 'none', md: 'block'}}
            ml={{md: 2, lg: 10}}
          >
            <UserMenuPopover backoffice={backoffice} />
          </Box>
        </HStack>
      </HStack>
    </Box>
  );
};
